export const DEFAULT_BUFFER_AHEAD_MS = 10_000;

export const DEFAULT_BUFFER_EDGE_TOLERANCE_MS = 100;

/** Near segment end, natural `ended` / auto-advance handles the handoff — skip in-tick stall. */
export const SEGMENT_END_BUFFER_STALL_SKIP_MS = 2_000;

export function shouldSkipPlaybackBufferStallAtPosition(
	positionInSegmentMs: number,
	segmentDurationMs: number,
): boolean {
	if (segmentDurationMs <= 0) {
		return true;
	}
	const remainingMs = Math.max(0, segmentDurationMs - positionInSegmentMs);
	return remainingMs <= SEGMENT_END_BUFFER_STALL_SKIP_MS;
}

export interface BufferAheadOptions {
	minBufferAheadMs?: number;
	bufferEdgeToleranceMs?: number;
}

export function resolveRequiredBufferAheadMs(
	positionMs: number,
	segmentDurationMs: number,
	minBufferAheadMs = DEFAULT_BUFFER_AHEAD_MS,
): number {
	if (segmentDurationMs <= 0) {
		return 0;
	}
	const remainingMs = Math.max(0, segmentDurationMs - positionMs);
	return Math.min(minBufferAheadMs, remainingMs);
}

export interface BufferedTimeRange {
	startMs: number;
	endMs: number;
}

export function resolveBufferedRangeContainingPosition(
	audio: HTMLMediaElement,
	positionMs: number,
): BufferedTimeRange | null {
	const { buffered } = audio;
	const positionSec = positionMs / 1000;

	for (let index = 0; index < buffered.length; index += 1) {
		const startSec = buffered.start(index);
		const endSec = buffered.end(index);
		if (positionSec >= startSec && positionSec <= endSec) {
			return {
				startMs: startSec * 1000,
				endMs: endSec * 1000,
			};
		}
	}

	return null;
}

export function resolveSegmentDurationMsFromAudio(
	audio: HTMLMediaElement,
	fallbackDurationMs?: number,
): number {
	if (Number.isFinite(audio.duration) && audio.duration > 0) {
		return audio.duration * 1000;
	}
	return fallbackDurationMs ?? 0;
}

export function isBufferedAheadOfPosition(
	audio: HTMLMediaElement,
	positionMs: number,
	segmentDurationMs: number,
	options: BufferAheadOptions = {},
): boolean {
	const minBufferAheadMs =
		options.minBufferAheadMs ?? DEFAULT_BUFFER_AHEAD_MS;
	const toleranceMs =
		options.bufferEdgeToleranceMs ?? DEFAULT_BUFFER_EDGE_TOLERANCE_MS;
	const requiredAheadMs = resolveRequiredBufferAheadMs(
		positionMs,
		segmentDurationMs,
		minBufferAheadMs,
	);
	const requiredEndMs = positionMs + requiredAheadMs;
	const range = resolveBufferedRangeContainingPosition(audio, positionMs);

	if (!range) {
		return requiredAheadMs <= 0;
	}

	return range.endMs >= requiredEndMs - toleranceMs;
}

/** Segment timeline percent (0–100) through which buffer-ahead at `positionMs` is satisfied. */
export function resolveBufferRevealTargetPercentAtPosition(
	audio: HTMLMediaElement,
	positionMs: number,
	segmentDurationMs: number,
): number {
	if (segmentDurationMs <= 0) {
		return 0;
	}
	const range = resolveBufferedRangeContainingPosition(audio, positionMs);
	if (!range) {
		return 0;
	}
	const bufferedEndInSegmentMs = Math.min(segmentDurationMs, range.endMs);
	return Math.max(
		0,
		Math.min(100, (bufferedEndInSegmentMs / segmentDurationMs) * 100),
	);
}

export function isAtBufferedBoundary(
	audio: HTMLMediaElement,
	positionMs: number,
	options: BufferAheadOptions = {},
): boolean {
	const toleranceMs =
		options.bufferEdgeToleranceMs ?? DEFAULT_BUFFER_EDGE_TOLERANCE_MS;
	const range = resolveBufferedRangeContainingPosition(audio, positionMs);

	if (!range) {
		return true;
	}

	return positionMs >= range.endMs - toleranceMs;
}

export function waitForBufferAheadOfPosition(
	audio: HTMLMediaElement,
	positionMs: number,
	segmentDurationMs: number,
	options: BufferAheadOptions = {},
): Promise<void> {
	if (
		isBufferedAheadOfPosition(audio, positionMs, segmentDurationMs, options)
	) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const finish = () => {
			cleanup();
			resolve();
		};

		const check = () => {
			if (
				isBufferedAheadOfPosition(
					audio,
					positionMs,
					segmentDurationMs,
					options,
				)
			) {
				finish();
			}
		};

		const cleanup = () => {
			audio.removeEventListener("progress", check);
			audio.removeEventListener("loadeddata", check);
			audio.removeEventListener("canplay", check);
			audio.removeEventListener("canplaythrough", check);
			audio.removeEventListener("durationchange", check);
		};

		audio.addEventListener("progress", check);
		audio.addEventListener("loadeddata", check);
		audio.addEventListener("canplay", check);
		audio.addEventListener("canplaythrough", check);
		audio.addEventListener("durationchange", check);
		check();
	});
}
